import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFiscalYearService } from '../api/services/fiscalYearService';
import { FiscalYearFormData } from '../types/fiscalYear';

export function useFiscalYears(orgId: string) {
  const fiscalYearService = useFiscalYearService();

  return useQuery({
    queryKey: ['organization', orgId, 'fiscal-years'],
    queryFn: () => fiscalYearService.getFiscalYears(),
    enabled: !!orgId,
  });
}

export function useCreateFiscalYear(orgId: string) {
  const queryClient = useQueryClient();
  const fiscalYearService = useFiscalYearService();

  return useMutation({
    mutationFn: (data: FiscalYearFormData) =>
      fiscalYearService.createFiscalYear(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['organization', orgId, 'fiscal-years'],
      });
    },
  });
}

export function useUpdateFiscalYear(orgId: string) {
  const queryClient = useQueryClient();
  const fiscalYearService = useFiscalYearService();

  return useMutation({
    mutationFn: ({ fyId, data }: { fyId: string; data: FiscalYearFormData }) =>
      fiscalYearService.updateFiscalYear(fyId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['organization', orgId, 'fiscal-years'],
      });
    },
  });
}

export function useDeleteFiscalYear(orgId: string) {
  const queryClient = useQueryClient();
  const fiscalYearService = useFiscalYearService();

  return useMutation({
    mutationFn: (fyId: string) => fiscalYearService.deleteFiscalYear(fyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['organization', orgId, 'fiscal-years'],
      });
    },
  });
}

export function useSetActiveFiscalYear(orgId: string) {
  const queryClient = useQueryClient();
  const fiscalYearService = useFiscalYearService();

  return useMutation({
    mutationFn: (fyId: string) => fiscalYearService.setActiveFiscalYear(fyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['organization', orgId, 'fiscal-years'],
      });
    },
  });
}
